// What the data looks like after each stage of the pipeline
export const FLOW: Record<string, string[]> = {
  afterTokenizer: [
    '"The"      → 464',
    '"capital"  → 3361',
    '"of"       → 286',
    '"France"   → 3182',
    '"is"       → 318',
  ],
  afterTokenEmbeddings: [
    '464  → [0.21, -0.54, ...]',
    '3361 → [-0.33, 0.12, ...]',
    '...',
  ],
  afterPositionalEncoding: [
    'pos 0: [0.21,  0.30, ...]',
    'pos 1: [-0.33, 0.96, ...]',
    '...',
  ],
  afterTransformer: [
    '5 × 768 contextual vectors',
  ],
  afterUnembedding: [
    '50,257 logit scores',
  ],
  afterSoftmax: [
    '"Paris"   94.3%',
    '"Lyon"     2.1%',
    '"France"   1.4%',
  ],
  afterSampling: [
    'selected: "Paris"',
  ],
};

export interface InfoToggle { q: string; a: string }
export interface InfoEntry { title: string; body: string; toggles?: InfoToggle[] }

export const INFO: Record<string, InfoEntry> = {
  input: {
    title: 'Input Text',
    body: 'The raw text prompt you provide — for example, "What is the capital of France?". This is the starting point for everything the model does.',
  },
  output: {
    title: 'Output Text',
    body: "The model's response, generated one token at a time. Each new token is fed back as input to generate the next one, until the response is complete.",
  },
  llm: {
    title: 'Large Language Model',
    body: 'A neural network trained on vast amounts of text to predict the next token in a sequence. Click to expand and see what happens inside.',
  },
  tokenizer: {
    title: 'Tokenizer',
    body: 'Splits the input into tokens — small chunks, usually subword units. "Tokenization" might become ["Token", "ization"]. Each token maps to a unique integer ID.',
    toggles: [
      {
        q: 'Why tokenize at all?',
        a: 'Working with raw characters makes sequences very long and slow to process. Working with whole words creates a huge vocabulary and struggles with new or rare words. Subword tokenization is a middle ground: common words stay as one token, rare words get split into recognisable pieces. "unhappiness" might become ["un", "happiness"], so the model can still reason about the parts.',
      },
      {
        q: 'Why do tokens sometimes look weird?',
        a: 'Tokenizers are trained on large text corpora, so the splits reflect frequency patterns in that data rather than linguistic rules. A space is often merged into the following token, punctuation may form its own token, and the same word can tokenise differently depending on capitalisation or surrounding context.',
      },
    ],
  },
  embeddings: {
    title: 'Embeddings + Positional Encoding',
    body: 'Each token ID is converted into a dense vector of numbers, and positional information is added so the model knows the order of tokens. Click to expand.',
  },
  tokenembeddings: {
    title: 'Token Embeddings',
    body: 'Each token ID is looked up in a learned table and converted into a dense vector of numbers (the embedding). Tokens with similar meanings end up with similar vectors — so "king" and "queen" are closer together in this space than "king" and "bicycle".',
  },
  positionalencoding: {
    title: 'Positional Encoding',
    body: 'Since attention has no built-in sense of order, a positional encoding is added to each token embedding to encode its position in the sequence. Without this, the model would treat "cat sat on the mat" and "mat the on sat cat" identically.',
  },
  transformer: {
    title: 'Transformer Block × N',
    body: 'The core computational unit, stacked many times. Each block has a multi-head attention layer followed by a feed-forward network. Large models have dozens to hundreds of these. Click to expand.',
  },
  residual: {
    title: 'Residual Stream',
    body: 'A vector that flows through the entire transformer, unchanged by default. Each sub-layer (attention and FFN) reads from this stream, computes a small update, and adds it back. This "skip connection" lets gradients flow cleanly during training and lets early layers communicate directly with later ones.',
  },
  layernorm: {
    title: 'Layer Normalisation',
    body: 'Before each sub-layer (attention or FFN), the residual stream is normalised so that values have roughly unit variance. This stabilises training and lets each layer operate on consistently-scaled inputs, regardless of how large the values in the stream have grown.',
  },
  attention: {
    title: 'Multi-Head Attention',
    body: 'Each token attends to every other token, learning which parts of the sequence are relevant. Multiple heads run in parallel, each capturing different types of relationships. This is how the model understands context.',
    toggles: [
      {
        q: 'What does "attend" actually mean?',
        a: 'Each token computes a query (what am I looking for?), and every token computes a key (what do I contain?) and a value (what should I contribute?). Attention scores are computed by matching each query against all keys. High-scoring tokens contribute more of their value to the output. So for the word "it" in "the cat sat because it was tired", attention lets the model link "it" back to "cat".',
      },
      {
        q: 'Why multiple heads?',
        a: 'A single attention head can only capture one type of relationship at a time. Multiple heads run in parallel with different learned projections, so one head might track subject–verb agreement while another tracks coreference. Their outputs are concatenated and projected down, combining all the perspectives.',
      },
    ],
  },
  addnorm: {
    title: 'Add & Norm',
    body: 'The original input to the sub-layer is added back to its output (residual connection), then layer normalisation is applied. Residual connections let gradients flow cleanly during training; layer norm stabilises the activations.',
  },
  ffn: {
    title: 'Feed-Forward Network',
    body: "After attention, each token's representation passes independently through a small two-layer network. This adds non-linearity and lets the model process what it gathered during attention.",
  },
  unembedding: {
    title: 'Unembedding Layer',
    body: "The inverse of the token embedding step. The final hidden vector for the last token is multiplied by the unembedding matrix (often the transpose of the embedding table), producing one score (logit) per vocabulary word.",
  },
  outputlayer: {
    title: 'Output Layer',
    body: 'Converts the transformer\'s final hidden states into a probability distribution over the vocabulary, then samples a token. Click to expand.',
  },
  softmax: {
    title: 'Softmax',
    body: 'Converts the raw logit scores into probabilities that sum to 1. A high temperature makes the distribution flatter (more random); a low temperature sharpens it (more confident).',
  },
  sampling: {
    title: 'Token Sampling',
    body: 'A token is drawn from the probability distribution. Common strategies: greedy (always pick the highest probability), top-k (sample from the k most likely), or nucleus/top-p (sample from the smallest set of tokens whose probabilities sum to p).',
  },
};
